import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../../../config/api';
import { Button } from '@/Components/ui/button';
import { Card, CardContent } from '@/Components/ui/card';
import { Loader2, CheckCircle2, XCircle, X } from 'lucide-react';

export default function CandidateOfferResponse() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [decision, setDecision] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState('');
  const requestedDecision = searchParams.get('response');

  useEffect(() => {
    let active = true;
    const loadOffer = async () => {
      try {
        const response = await fetch(getApiUrl(`/api/offer-response/${encodeURIComponent(token)}`));
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'This offer could not be opened.');
        if (active) setOffer(data);
      } catch (loadError) {
        if (active) setError(loadError.message || 'This offer could not be opened.');
      } finally {
        if (active) setLoading(false);
      }
    };
    loadOffer();
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (
      offer?.status === 'released' &&
      (requestedDecision === 'accept' || requestedDecision === 'reject')
    ) {
      setDecision(requestedDecision);
    }
  }, [offer?.status, requestedDecision]);

  const submitDecision = async () => {
    if (!decision) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(getApiUrl(`/api/offer-response/${encodeURIComponent(token)}/respond`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, rejection_reason: rejectionReason }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Your response could not be saved.');
      setOffer((current) => ({ ...current, status: data.status }));
      setResult(data.message || 'Your response has been recorded.');
      setDecision('');
      setRejectionReason('');
    } catch (submitError) {
      setError(submitError.message || 'Your response could not be saved.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <main className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin" /></main>;
  }

  if (error && !offer) {
    return <main className="min-h-screen grid place-items-center p-5"><Card className="max-w-lg w-full"><CardContent className="p-8 text-center text-destructive">{error}</CardContent></Card></main>;
  }

  const isOpen = offer?.status === 'released';
  const responseLabel = offer?.status === 'accepted' ? 'Offer accepted' : offer?.status === 'rejected' ? 'Offer rejected' : offer?.status === 'expired' ? 'Offer expired' : 'Offer response recorded';
  const closeResponseTab = () => window.close();

  return (
    <main className="min-h-screen bg-muted/30 grid place-items-center p-4">
      <Card className="w-full max-w-2xl shadow-sm">
        <div className="hidden">
          <p className="text-2xl">Offer of Employment</p>
          <p className="text-sm text-muted-foreground">{offer?.company_name} · {offer?.job_title}</p>
        </div>
        <CardContent className="p-6 space-y-4">
          {error && <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}

          {isOpen ? (
            <section className="border rounded-lg p-4 space-y-4">
              {!decision ? (
                <>
                  <p className="font-medium">Please choose your response.</p>
                  <div className="flex flex-wrap gap-3">
                    <Button disabled={submitting} onClick={() => { setError(''); setDecision('accept'); }} className="bg-green-700 hover:bg-green-800"><CheckCircle2 className="h-4 w-4 mr-2" />Accept Offer</Button>
                    <Button variant="outline" disabled={submitting} onClick={() => { setError(''); setDecision('reject'); }} className="text-destructive"><XCircle className="h-4 w-4 mr-2" />Reject Offer</Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-medium">Confirm that you want to {decision === 'accept' ? 'accept' : 'reject'} this offer.</p>
                  {decision === 'reject' && (
                    <textarea disabled={submitting} className="w-full min-h-24 rounded-md border p-3 text-sm disabled:cursor-not-allowed disabled:opacity-60" value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} placeholder="Optional reason for rejecting the offer" />
                  )}
                  <div className="flex gap-3">
                    <Button disabled={submitting} onClick={submitDecision} className={decision === 'accept' ? 'bg-green-700 hover:bg-green-800' : ''}>
                      {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</> : <>Confirm {decision === 'accept' ? 'Acceptance' : 'Rejection'}</>}
                    </Button>
                    <Button variant="outline" disabled={submitting} onClick={() => { setDecision(''); setRejectionReason(''); }}>Cancel</Button>
                  </div>
                </>
              )}
            </section>
          ) : (
            <section className="relative border rounded-lg p-4">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={closeResponseTab}
                className="absolute right-2 top-2 h-8 w-8"
                aria-label="Close this tab"
                title="Close this tab"
              >
                <X className="h-4 w-4" />
              </Button>
              {result ? (
                <p role="status" className="mr-8 rounded-md bg-green-50 text-green-800 p-3 text-sm">{result}</p>
              ) : (
                <div className="mr-8 rounded-md bg-muted p-4 text-sm font-medium">{responseLabel}</div>
              )}
            </section>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
